import { useRef, useState } from 'react';

/**
 * 🖼️ مكوّن رفع الصور
 *    - واحدة أو متعددة
 *    - drag & drop + click to upload
 *    - معاينة فورية
 *    - شريط تقدّم
 *    - حذف
 *    - يستخدم /api/uploads (single) أو /api/uploads/multiple
 */
type UploadResponse = {
  id: string;
  url: string;
  thumbnailUrl?: string | null;
  filename: string;
};

interface ImageUploadProps {
  /** الصور الحالية (URLs) */
  value: string[];
  /** يُستدعى عند تغيير القائمة */
  onChange: (urls: string[]) => void;
  /** الحد الأقصى للصور */
  maxImages?: number;
  /** غرض الرفع (لتحديد المجلد والحجم) */
  purpose?: 'PROPERTY_IMAGE' | 'PROPERTY_COVER' | 'PROFILE_IMAGE' | 'AVATAR' | 'ANNOUNCEMENT_IMAGE' | 'OTHER';
  /** عنوان الـ label */
  label?: string;
  /** نص توضيحي تحت العنوان */
  hint?: string;
  /** صورة واحدة فقط (للـ cover) */
  single?: boolean;
}

export function ImageUpload({
  value,
  onChange,
  maxImages = 10,
  purpose = 'PROPERTY_IMAGE',
  label,
  hint,
  single = false,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const remaining = single ? (value.length === 0 ? 1 : 0) : Math.max(0, maxImages - value.length);

  const uploadFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setError(null);

    // فلترة الصور فقط
    const images = arr.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      setError('الرجاء اختيار صور فقط');
      return;
    }
    if (images.length > remaining) {
      setError(`يمكنك رفع ${remaining} صورة فقط (الباقية)`);
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const newUrls: string[] = [];
      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('purpose', purpose);

        // استخدم fetch مباشرة للحصول على progress
        const res = await fetch('/api/uploads', {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.error?.message ?? `فشل رفع ${file.name}`);
        }
        const json = await res.json() as { data: UploadResponse };
        newUrls.push(json.data.url);
        setProgress(Math.round(((i + 1) / images.length) * 100));
      }

      onChange(single ? [newUrls[0]] : [...value, ...newUrls]);
    } catch (e) {
      setError((e as Error).message ?? 'فشل الرفع');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files);
  };

  const removeImage = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const moveImage = (from: number, to: number) => {
    if (to < 0 || to >= value.length) return;
    const arr = [...value];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    onChange(arr);
  };

  return (
    <div>
      {label && <label className="block text-sm font-bold text-ink mb-1">{label}</label>}
      {hint && <p className="text-[11px] text-ink-muted mb-2">{hint}</p>}

      {/* 📥 منطقة الرفع (drag & drop + click) */}
      {remaining > 0 && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition ${
            dragOver
              ? 'border-gold bg-gold-soft'
              : uploading
              ? 'border-navy bg-navy-soft'
              : 'border-gray-300 bg-gray-50 hover:border-navy hover:bg-navy-soft'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/heic"
            multiple={!single}
            onChange={(e) => e.target.files && uploadFiles(e.target.files)}
            className="hidden"
          />

          {uploading ? (
            <div>
              <div className="text-3xl mb-2 animate-pulse">⏳</div>
              <p className="text-sm font-bold text-navy-dark mb-2">جاري رفع الصور…</p>
              <div className="max-w-xs mx-auto bg-white rounded-full h-2 overflow-hidden">
                <div className="bg-gradient-to-l from-gold-dark to-gold h-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-ink-muted mt-1">{progress}%</p>
            </div>
          ) : (
            <div>
              <div className="text-5xl mb-2">📸</div>
              <p className="text-sm font-bold text-navy-dark">
                {dragOver ? 'أفلت الصور هنا' : 'اضغط أو اسحب الصور لرفعها'}
              </p>
              <p className="text-xs text-ink-muted mt-1">
                JPG, PNG, WebP, HEIC · حتى 5 ميجا للصورة
                {!single && ` · يمكنك رفع ${remaining} صورة`}
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl p-2 mt-2">
          ⚠️ {error}
        </div>
      )}

      {/* 🖼️ معاينة الصور المرفوعة */}
      {value.length > 0 && (
        <div className={`grid gap-3 mt-3 ${single ? 'grid-cols-1 max-w-sm' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'}`}>
          {value.map((url, i) => (
            <div key={i} className="relative group rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-50 aspect-square">
              <img src={url} alt="" className="w-full h-full object-cover" />

              {/* 🏷️ شارة "رئيسية" على أول صورة (لو متعددة) */}
              {!single && i === 0 && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-gold text-white text-[10px] font-bold shadow-lg">
                  ⭐ رئيسية
                </div>
              )}

              {/* 🛠️ أدوات (تظهر عند الـ hover) */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                {!single && i > 0 && (
                  <button
                    onClick={() => moveImage(i, 0)}
                    className="w-8 h-8 rounded-full bg-white shadow text-base hover:bg-gold hover:text-white transition"
                    title="اجعلها الرئيسية"
                  >
                    ⭐
                  </button>
                )}
                {!single && i > 0 && (
                  <button
                    onClick={() => moveImage(i, i - 1)}
                    className="w-8 h-8 rounded-full bg-white shadow text-base hover:bg-navy hover:text-white transition"
                    title="حرّك للأمام"
                  >
                    ↑
                  </button>
                )}
                {!single && i < value.length - 1 && (
                  <button
                    onClick={() => moveImage(i, i + 1)}
                    className="w-8 h-8 rounded-full bg-white shadow text-base hover:bg-navy hover:text-white transition"
                    title="حرّك للخلف"
                  >
                    ↓
                  </button>
                )}
                <button
                  onClick={() => removeImage(i)}
                  className="w-8 h-8 rounded-full bg-white shadow text-base hover:bg-red-600 hover:text-white transition"
                  title="حذف"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!single && value.length > 0 && (
        <p className="text-[11px] text-ink-muted mt-2">
          📌 الصورة الأولى هي صورة الغلاف الرئيسية. يمكنك إعادة ترتيب الصور بالأزرار أعلاها.
        </p>
      )}
    </div>
  );
}
